import { Random } from './Random';

export namespace ArrayHelper {
    /**
     * Shallow array value comparison. Returns TRUE if the array values are identical, FALSE if array values are different.
     * @param array1
     * @param array2
     */
    export function compare<T>(
        array1: T[],
        array2: T[],
        compareElementOverride?: (element1: T, element2: T) => boolean
    ): boolean {
        const compareElement = compareElementOverride ?? defaultElementCompare;
        if (array1 === array2) return true;
        if (array1 == null || array2 == null) return false;
        if (array1.length !== array2.length) return false;
        for (let i = 0; i < array2.length; ++i) {
            if (!compareElement(array1[i], array2[i])) return false;
        }
        return true;

        function defaultElementCompare(element1: T, element2: T) {
            return element1 === element2;
        }
    }

    export function range(start: number, end: number, step: number = 1): number[] {
        if (start > end) step *= -1;
        const length = Math.floor(Math.abs((end - start) / step)) + 1;
        return Array.from(Array(length), (x, index) => start + index * step);
    }

    export function addIfNeeded<T>(array: T[], item: T): T[] {
        return array.indexOf(item) === -1 ? [...array, item] : array;
    }

    export function getRandom<T>(array: T[]): T {
        if (!array || array.length === 0) return undefined;
        return array[Math.floor(Math.random() * array.length)];
    }

    export function getClamped<T>(array: ReadonlyArray<T>, index: number): T | undefined {
        if (array.length === 0) return;
        return array[Math.min(Math.max(index, 0), array.length - 1)];
    }

    /**
     * Shuffle the specified array.
     * @param array The array to be shuffled.
     * @param boolean Force the array to derange (values are forced into a new position).
     */
    export function shuffle<T>(array: T[], noRepeat: boolean = false) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Random.value() * (i + (noRepeat ? 0 : 1)));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    /**
     * Return a new array where entires added to `oldArray` (deducted from `newArray`) are grouped
     * based on `canBeGrouped` and `group` (grouping at position of 1st occurence)
     */
    export function grouped<T>(
        oldArray: ReadonlyArray<T>,
        newArray: ReadonlyArray<T>,
        canBeGrouped: (a: T, b: T) => boolean,
        group: (acc: T, e: T) => T
    ) {
        const newElements = newArray.filter(e => !oldArray.includes(e));
        const result = [...newArray];
        for (let i = 0; i < result.length - 1; i += 1) {
            if (!newElements.includes(result[i])) continue;
            for (let j = i + 1; j < result.length; j += 1) {
                if (!newElements.includes(result[j])) continue;
                if (canBeGrouped(result[i], result[j])) {
                    result[i] = group(result[i], result[j]);
                    result.splice(j--, 1);
                }
            }
        }
        return result;
    }

    /**
     * @example
     * myArray.filter(ArrayHelper.filterOnce)
     */
    export function filterOnce<T>(element: T, index: number, array: Array<T>) {
        return array.indexOf(element) === index;
    }

    export function remove<T>(array: Array<T>, elementToRemove: T) {
        return array.filter(element => element === elementToRemove);
    }

    export function removeIndex<T>(array: Array<T>, indexToRemove: number) {
        return array.filter((element, index) => index !== indexToRemove);
    }

    export function flatten<T>(array: Array<Array<T>>): Array<T> {
        const newArray = new Array<T>();
        array.forEach(subArray => {
            newArray.push(...subArray);
        });

        return newArray;
    }

    export function move<T>(array: T[], old_index: number, new_index: number) {
        if (new_index >= array.length) {
            var k = new_index - array.length + 1;
            while (k--) {
                array.push(undefined);
            }
        }
        array.splice(new_index, 0, array.splice(old_index, 1)[0]);
        return array;
    }

    export function insert<T>(array: T[], index: number, item: T): T[] {
        return [...array.slice(0, index), item, ...array.slice(index)];
    }

    /**
     * Returns new array with element at given position changed with the given function
     * Does not mutate original array.
     */
    export function changeElement<T>(
        array: Array<T>,
        indexOfElementToChange: number,
        fn: (oldElement: T) => T
    ): Array<T> {
        return array.map((element, i) => {
            if (i === indexOfElementToChange) return fn(element);
            return element;
        });
    }

    /** Filters the array, returning only the duplicate values */
    export function getDuplicates<T>(array: Array<T>): Array<T> {
        return array.filter((value, index) => array.indexOf(value) !== index);
    }

    export interface IPriority {
        priority?: number;
    }

    export function sortByHighestPriority<T extends IPriority>(array: Array<T>): Array<T> {
        return array.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    }

    export function getHightestPriorityLevel<T extends IPriority>(array: Array<T>): number {
        return array.reduce((a, b) => Math.max(a, b.priority ?? 0), 0);
    }
}
export type tVector4 = [number, number, number, number];
export type tVector2 = [number, number];
